import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { FulfillmentType, PrintDocumentType } from '@/lib/shared-types';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  FLEET_ROUTES,
  type CreateRoutingRuleBody,
  type FleetDestination,
  type FleetRoutingRule,
} from '../fleetApi';
import { apiMessage } from '../fleetHelpers';

export interface StationRef {
  id: string;
  name: string;
  code?: string | null;
  branchId?: string | null;
}

export interface CategoryRef {
  id: string;
  name: string;
}

const DOCUMENT_TYPES = Object.values(PrintDocumentType);
const FULFILLMENT_TYPES = Object.values(FulfillmentType);

/**
 * One routing rule: which paper a document the kitchen has already routed comes
 * out on.
 *
 * The station field is the one worth being careful about, and its label says so
 * out loud: it **matches** the station the kitchen chose, it does not assign
 * one. `KitchenStation` and `OrderItem.stationId` stay the only authority on
 * where a dish is made — a rule that could re-decide it would produce a board
 * and a ticket that disagree, with nothing able to say which is right.
 *
 * Every criterion left blank means "any". A rule with all four blank matches
 * everything, which is a legitimate and useful thing to write — it is the
 * catch-all a venue points at its one printer — so it is allowed and simply
 * described as such.
 *
 * `priority` beats specificity, and that ordering is deliberate: specificity is
 * the automatic rule that makes a category rule win over a blanket one without
 * anybody numbering them, and `priority` is the manual lever for overriding the
 * system for one service.
 */
export default function RoutingRuleFormDialog({
  open,
  rule,
  destinations,
  stations,
  categories,
  canReadStations,
  canReadCategories,
  locale,
  onClose,
  onSaved,
}: {
  open: boolean;
  rule: FleetRoutingRule | null;
  destinations: FleetDestination[];
  stations: StationRef[];
  categories: CategoryRef[];
  canReadStations: boolean;
  canReadCategories: boolean;
  locale: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();

  const [destinationId, setDestinationId] = useState('');
  const [documentType, setDocumentType] = useState('');
  const [stationId, setStationId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [fulfillmentType, setFulfillmentType] = useState('');
  const [priority, setPriority] = useState('0');
  const [isActive, setIsActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setDestinationId(rule?.destinationId ?? destinations[0]?.id ?? '');
    setDocumentType(rule?.documentType ?? '');
    setStationId(rule?.stationId ?? '');
    setCategoryId(rule?.categoryId ?? '');
    setFulfillmentType(rule?.fulfillmentType ?? '');
    setPriority(rule ? String(rule.priority) : '0');
    setIsActive(rule?.isActive ?? true);
  }, [open, rule, destinations]);

  const submit = async () => {
    if (!destinationId) {
      setError(t('fleet.rules.destinationRequired'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const criteria = {
        documentType: (documentType || undefined) as CreateRoutingRuleBody['documentType'],
        stationId: stationId || undefined,
        categoryId: categoryId || undefined,
        fulfillmentType: (fulfillmentType || undefined) as CreateRoutingRuleBody['fulfillmentType'],
        priority: Number(priority) || 0,
      };
      if (rule) {
        await api.patch(FLEET_ROUTES.routingRule(rule.id), { ...criteria, isActive });
        toast.success(t('fleet.rules.updated'));
      } else {
        await api.post(FLEET_ROUTES.routingRules, { ...criteria, destinationId });
        toast.success(t('fleet.rules.created'));
      }
      onSaved();
      onClose();
    } catch (requestError) {
      setError(apiMessage(requestError, t('fleet.errors.actionFailed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={rule ? t('fleet.rules.editTitle') : t('fleet.rules.addTitle')}
      description={t('fleet.rules.formHint')}
      className="max-w-2xl"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button disabled={busy} onClick={() => void submit()}>
            {t('common.save')}
          </Button>
        </div>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label={t('fleet.rules.printsAt')}
          required
          hint={t('fleet.rules.destinationHint')}
        >
          <Select
            value={destinationId}
            disabled={Boolean(rule)}
            onChange={(event) => setDestinationId(event.target.value)}
          >
            <option value="">{t('fleet.rules.chooseDestination')}</option>
            {destinations.map((destination) => (
              <option key={destination.id} value={destination.id}>
                {destination.name} — {t(`fleet.role.${destination.role}`)}
              </option>
            ))}
          </Select>
        </Field>

        <Field label={t('fleet.spool.document')} hint={t('fleet.rules.anyHint')}>
          <Select value={documentType} onChange={(event) => setDocumentType(event.target.value)}>
            <option value="">{t('fleet.rules.any')}</option>
            {DOCUMENT_TYPES.map((option) => (
              <option key={option} value={option}>
                {t(`fleet.documentType.${option}`)}
              </option>
            ))}
          </Select>
        </Field>

        <Field
          label={t('fleet.rules.station')}
          hint={
            canReadStations ? t('fleet.rules.stationHint') : t('fleet.rules.stationUnavailable')
          }
        >
          <Select
            value={stationId}
            disabled={!canReadStations}
            onChange={(event) => setStationId(event.target.value)}
          >
            <option value="">{t('fleet.rules.any')}</option>
            {[...stations]
              .sort((a, b) => a.name.localeCompare(b.name, locale))
              .map((station) => (
                <option key={station.id} value={station.id}>
                  {station.name}
                </option>
              ))}
          </Select>
        </Field>

        <Field
          label={t('fleet.rules.category')}
          hint={
            canReadCategories ? t('fleet.rules.categoryHint') : t('fleet.rules.categoryUnavailable')
          }
        >
          <Select
            value={categoryId}
            disabled={!canReadCategories}
            onChange={(event) => setCategoryId(event.target.value)}
          >
            <option value="">{t('fleet.rules.any')}</option>
            {[...categories]
              .sort((a, b) => a.name.localeCompare(b.name, locale))
              .map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
          </Select>
        </Field>

        <Field label={t('fleet.rules.fulfillment')} hint={t('fleet.rules.anyHint')}>
          <Select
            value={fulfillmentType}
            onChange={(event) => setFulfillmentType(event.target.value)}
          >
            <option value="">{t('fleet.rules.any')}</option>
            {FULFILLMENT_TYPES.map((option) => (
              <option key={option} value={option}>
                {t(`fleet.fulfillment.${option}`)}
              </option>
            ))}
          </Select>
        </Field>

        <Field label={t('fleet.rules.priority')} hint={t('fleet.rules.priorityHint')}>
          <Input
            value={priority}
            inputMode="numeric"
            onChange={(event) => setPriority(event.target.value.replace(/[^\d-]/g, ''))}
          />
        </Field>

        {rule && (
          <Field label={t('fleet.common.status')}>
            <Select
              value={isActive ? 'true' : 'false'}
              onChange={(event) => setIsActive(event.target.value === 'true')}
            >
              <option value="true">{t('fleet.rules.active')}</option>
              <option value="false">{t('fleet.rules.inactive')}</option>
            </Select>
          </Field>
        )}
      </div>

      {error && (
        <p role="alert" className="mt-4 text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
    </Dialog>
  );
}
